import type { Reservation } from '@store';
import { Linking, Platform } from 'react-native';

const isIphoneOnWeb = () => {
  const userAgent =
    typeof navigator === 'undefined' ? '' : navigator.userAgent ?? '';
  return /iPhone|iPad/i.test(userAgent) && Platform.OS === 'web';
};

const shouldUseAppleMaps = () => Platform.OS === 'ios' || isIphoneOnWeb();

export const getCombinedAddress = (
  reservationProperty: Reservation['property'],
) => {
  const addressParts = [
    reservationProperty.address,
    reservationProperty.address2,
    reservationProperty.city,
    reservationProperty.state,
    reservationProperty.postalCode,
    reservationProperty.country,
  ];

  return addressParts
    .map(addressPart => addressPart?.trim())
    .filter(Boolean)
    .join(', ');
};

const getValidCoordinates = (property: Reservation['property']) => {
  const latitude = Number(property.latitude);
  const longitude = Number(property.longitude);
  const isValid =
    Boolean(property.latitude?.trim() && property.longitude?.trim()) &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180 &&
    (latitude !== 0 || longitude !== 0);

  return isValid ? `${latitude},${longitude}` : undefined;
};

const getMapsUrl = (property: Reservation['property']) => {
  const coordinates = getValidCoordinates(property);
  const destination = coordinates || getCombinedAddress(property);
  const encodedDestination = encodeURIComponent(destination);

  if (!shouldUseAppleMaps()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodedDestination}`;
  }

  return coordinates
    ? `http://maps.apple.com/?ll=${coordinates}&q=${encodeURIComponent(
        property.name || coordinates,
      )}`
    : `http://maps.apple.com/?address=${encodedDestination}`;
};

export const openMaps = (property: Reservation['property']) =>
  Linking.openURL(getMapsUrl(property));
